import { type Project, useStores } from '@littlebird/sdk';
import { getMeetingGroupLabel } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { type ComponentProps, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  type BaseModalFCProps,
  type ModalFC,
  ModalsManager,
} from '@/components/modals/modalsManager';
import { Button, XStack, YStack } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Empty } from '@/components/ui/EmptyState';
import { EntityIcon } from '@/components/ui/icon-picker';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { useLastUserMessage } from '@/features/chat/chat.store';
import {
  getDisplayAttendees,
  storeAttendeesToDisplayAttendees,
} from '@/features/meetings/meeting-mapper';
import { formatAttendees } from '@/features/meetings/meetings-list.utils';
import {
  type CalendarEvent,
  PLACEHOLDER_MEETING_TITLE,
} from '@/features/meetings/meetings.types';
import { useListKeyboardNavigation } from '@/hooks/use-list-keyboard-navigation';
import { cn } from '@/lib/utils';

import { ProjectActivityToolbar } from './project-activity-toolbar';
import { useExistingItemsPicker } from './use-existing-items-picker';
import { useLoadAllProjectPages } from './use-load-all-project-pages';

export interface AddExistingItemsModalProps extends BaseModalFCProps {
  projectId: string;
}

export const AddExistingItemsModal: ModalFC<AddExistingItemsModalProps> =
  observer(function AddExistingItemsModal({ projectId, open, onOpenChange }) {
    const picker = useExistingItemsPicker(projectId, () => onOpenChange(false));
    useLoadAllProjectPages(open);

    const keyboard = useListKeyboardNavigation({
      idPrefix: 'add-existing',
      itemCount: picker.itemCount,
      onActivate: picker.activateIndex,
      resetKey: `${picker.filter}:${picker.hasSearchQuery}`,
    });

    const emptyCopy = picker.hasSearchQuery
      ? {
          title: 'No results found',
          description: 'Try a different search term.',
        }
      : {
          title: 'Nothing to add',
          description: 'All your chats and notes are already in this project.',
        };

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content
          showCloseButton={false}
          className="max-w-xl p-0 gap-0 overflow-hidden"
        >
          <Dialog.Title className="sr-only">
            Add existing chats and notes
          </Dialog.Title>
          <div className="border-b border-border p-3">
            <ProjectActivityToolbar
              autoFocus
              search={picker.search}
              onSearchChange={picker.setSearch}
              filter={picker.filter}
              onFilterChange={picker.setFilter}
              placeholder="Search chats and notes to add…"
              inputProps={{
                role: 'combobox',
                'aria-expanded': true,
                'aria-controls': keyboard.listId,
                'aria-activedescendant': keyboard.activeItemId,
                onKeyDown: keyboard.onKeyDown,
              }}
            />
          </div>
          <ScrollArea className="max-h-[420px]">
            <div
              ref={keyboard.listRef}
              id={keyboard.listId}
              role="listbox"
              aria-multiselectable
              aria-label="Chats and notes"
              className="p-1"
            >
              {picker.sections.map((section) => (
                <div
                  key={section.date}
                  role="group"
                  aria-labelledby={`add-existing-day-${section.date}`}
                >
                  <div
                    id={`add-existing-day-${section.date}`}
                    className="px-2 py-1.5 text-xs font-medium text-foreground-muted"
                  >
                    {getMeetingGroupLabel(section.date)}
                  </div>
                  {section.items.map(({ key, index, target }) =>
                    target.kind === 'thread' ? (
                      <ChatPickerRow
                        key={key}
                        id={keyboard.getItemId(index)}
                        index={index}
                        threadId={target.id}
                        isSelected={picker.isSelected(target)}
                        isActive={index === keyboard.activeIndex}
                        onToggle={picker.toggleTarget}
                      />
                    ) : (
                      <NotePickerRow
                        key={key}
                        id={keyboard.getItemId(index)}
                        index={index}
                        meetingId={target.id}
                        eventsById={picker.eventsById}
                        isSelected={picker.isSelected(target)}
                        isActive={index === keyboard.activeIndex}
                        onToggle={picker.toggleTarget}
                      />
                    )
                  )}
                </div>
              ))}
              {picker.sections.length === 0 && !picker.isLoading && (
                <Empty.Root variant="compact">
                  <Empty.Header>
                    <Empty.Title>{emptyCopy.title}</Empty.Title>
                    <Empty.Description>
                      {emptyCopy.description}
                    </Empty.Description>
                  </Empty.Header>
                </Empty.Root>
              )}
              <div ref={picker.chatSentinelRef} className="h-px" aria-hidden />
              <div
                ref={picker.meetingSentinelRef}
                className="h-px"
                aria-hidden
              />
            </div>
          </ScrollArea>
          <XStack className="justify-end gap-2 p-3 border-t border-border">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={picker.selectedCount === 0 || picker.isSubmitting}
              isLoading={picker.isSubmitting}
              onClick={() => void picker.confirmAdd()}
            >
              {picker.confirmLabel}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Root>
    );
  });

const ChatPickerRow = observer(function ChatPickerRow({
  id,
  index,
  threadId,
  isSelected,
  isActive,
  onToggle,
}: {
  id: string;
  index: number;
  threadId: string;
  isSelected: boolean;
  isActive: boolean;
  onToggle: (target: { kind: 'thread'; id: string }) => void;
}) {
  const { projects, threads } = useStores();
  const lastUserMessage = useLastUserMessage(threadId);
  const thread = threads.getById(threadId);

  if (!thread) return null;

  return (
    <PickerRow
      id={id}
      index={index}
      icon={
        <Icons.newChat className="size-3.5 shrink-0 text-foreground-muted" />
      }
      title={thread.title}
      subtitle={lastUserMessage}
      project={projects.getProjectForThread(threadId)}
      isSelected={isSelected}
      isActive={isActive}
      onToggle={() => onToggle({ kind: 'thread', id: threadId })}
    />
  );
});

const NotePickerRow = observer(function NotePickerRow({
  id,
  index,
  meetingId,
  eventsById,
  isSelected,
  isActive,
  onToggle,
}: {
  id: string;
  index: number;
  meetingId: string;
  eventsById: ReadonlyMap<string, CalendarEvent>;
  isSelected: boolean;
  isActive: boolean;
  onToggle: (target: { kind: 'meeting'; id: string }) => void;
}) {
  const { meetings, projects } = useStores();
  const meeting = meetings.get(meetingId);

  if (!meeting) return null;

  const event = meeting.eventId ? eventsById.get(meeting.eventId) : undefined;

  return (
    <PickerRow
      id={id}
      index={index}
      icon={
        <Icons.meetings className="size-3.5 shrink-0 text-foreground-muted" />
      }
      title={meeting.name ?? PLACEHOLDER_MEETING_TITLE}
      subtitle={formatAttendees(
        getDisplayAttendees(
          event?.attendees,
          storeAttendeesToDisplayAttendees(meeting.attendees)
        )
      )}
      project={projects.getProjectForMeeting(meetingId)}
      isSelected={isSelected}
      isActive={isActive}
      onToggle={() => onToggle({ kind: 'meeting', id: meetingId })}
    />
  );
});

function PickerRow({
  id,
  index,
  icon,
  title,
  subtitle,
  project,
  isSelected,
  isActive,
  onToggle,
}: {
  id: string;
  index: number;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  project?: Project | null;
  isSelected: boolean;
  isActive: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={isSelected}
      data-list-index={index}
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left',
        'hover:bg-secondary transition-colors',
        isActive && 'ring-1 ring-inset ring-border-strong'
      )}
    >
      <Checkbox
        checked={isSelected}
        className="pointer-events-none"
        tabIndex={-1}
        aria-hidden
      />
      {icon}
      <YStack className="min-w-0 flex-1 gap-0.5">
        <span className="truncate text-sm text-foreground">{title}</span>
        <span className="truncate text-xs text-foreground-muted">
          {subtitle || ' '}
        </span>
      </YStack>
      {project && (
        <XStack className="shrink-0 items-center gap-1 text-xs text-foreground-muted">
          <EntityIcon icon={project.icon} size={12} />
          <span className="max-w-24 truncate">{project.name}</span>
        </XStack>
      )}
    </button>
  );
}

export function AddExistingItemsButton({
  projectId,
  variant = 'outline',
}: {
  projectId: string;
  variant?: ComponentProps<typeof Button>['variant'];
}) {
  return (
    <Button
      size="sm"
      variant={variant}
      className="gap-1.5"
      onClick={() => ModalsManager.show(AddExistingItemsModal, { projectId })}
    >
      <Icons.plus className="size-3.5" />
      Add
    </Button>
  );
}
